
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type FinalPresentShaderUniforms = {
	uHdrScene: WebGLUniformLocation,
	uExposure: WebGLUniformLocation
};

export type FinalPresentShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type FinalPresentShaderSettings = string[];

export class FinalPresentShader extends ShaderBase<FinalPresentShaderUniforms, FinalPresentShaderAttributes> {

    private static fragmentShaderSource = `
// Always-on tonemapping pass. Reads the HDR scene FBO (linear, RGBA16F) and
// writes the ACES-tonemapped LDR result to either the FXAA input FBO or the
// canvas. Decoupled from bloom so every graphics preset combination produces
// a properly tonemapped image.

in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uHdrScene;
uniform float uExposure;

// ACES filmic tonemapping (Narkowicz 2015 approximation). Same operator that
// was previously embedded in the bloom composite shader.
vec3 aces_tonemap(vec3 x) {
	const float a = 2.51;
	const float b = 0.03;
	const float c = 2.43;
	const float d = 0.59;
	const float e = 0.14;
	return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

void main() {
	vec3 color = texture(uHdrScene, vTexCoord).rgb;
	color *= uExposure;
	color = aces_tonemap(color);
	fragColor = vec4(color, 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: FinalPresentShaderUniformsContainer;
    public attributesContainer: FinalPresentShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: FinalPresentShaderSettings) {
        super(ctx, FinalPresentShader.fragmentShaderSource, FinalPresentShader.vertexShaderSource, settings);
        this.uniformsContainer = new FinalPresentShaderUniformsContainer(ctx, this);
        this.attributesContainer = new FinalPresentShaderShaderAttributesContainer(ctx, this);
    }
}

class FinalPresentShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: FinalPresentShader) {
        super(ctx);
    }

    public setHdrScene(texture: Texture) : FinalPresentShaderUniformsContainer {
        this.setTextureValue('uHdrScene', this.shader.uniforms.uHdrScene, texture, 'sampler2D');
        return this;
    }

    public setExposure(value: number) : FinalPresentShaderUniformsContainer {
        this.setValue('uExposure', this.shader.uniforms.uExposure, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uHdrScene', uniform: this.shader.uniforms.uHdrScene, dataType: 'sampler2D' }
        ];
    }
}

class FinalPresentShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: FinalPresentShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : FinalPresentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : FinalPresentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}